import { SESSION_MAX_AGE_MS } from "./session";
import type { TreadmillPhase } from "./types";
import { voiceText, type VoiceLang } from "./voiceWords";

/**
 * The interval walk and a walk's spoken cues (ADR-063).
 *
 * The interval walk is the Shinshu method: an easy three minutes, then five rounds of three
 * minutes brisk and three easy. "Brisk" is an effort — breathing harder, still able to talk — and
 * never a speed: there is no machine outdoors to set, and a speed a parent cannot feel is one they
 * will overdo. The rounds are treadmill phases with no speed, so the player and the session record
 * carry them exactly as the treadmill's (ADR-027).
 *
 * The cue schedule is derived from the start, the planned length and the mode alone, so the page
 * and the counter app's wrapper (`WalkCues.kt`, ADR-064) compute the same seconds and either can
 * speak a cue without the other telling it.
 */

export const INTERVAL_ROUNDS = 5;
export const INTERVAL_WARM_SECS = 180;
export const INTERVAL_BRISK_SECS = 180;
export const INTERVAL_EASY_SECS = 180;
export const INTERVAL_TOTAL_SECS = INTERVAL_WARM_SECS + INTERVAL_ROUNDS * (INTERVAL_BRISK_SECS + INTERVAL_EASY_SECS);

export const BRISK_WORDS = "Breathing harder, still able to talk";
export const EASY_WORDS = "Catch your breath";

/** Warm-up · Brisk 1/5 · Easy 1/5 … Easy 5/5: eleven phases, 33 minutes, no speed. */
export function intervalPhases(): TreadmillPhase[] {
  const out: TreadmillPhase[] = [{ l: "Warm-up", s: 0, secs: INTERVAL_WARM_SECS, c: "rest" }];
  for (let r = 1; r <= INTERVAL_ROUNDS; r++) {
    out.push({ l: `Brisk ${r}/${INTERVAL_ROUNDS}`, s: 0, secs: INTERVAL_BRISK_SECS, c: "brisk" });
    out.push({ l: `Easy ${r}/${INTERVAL_ROUNDS}`, s: 0, secs: INTERVAL_EASY_SECS, c: "easy" });
  }
  return out;
}

/** The round a phase belongs to, 1–5; 0 for the warm-up. */
export function intervalRound(idx: number): number {
  return idx <= 0 ? 0 : Math.min(INTERVAL_ROUNDS, Math.ceil(idx / 2));
}

/** "Warm-up", "Brisk 2 of 5", "Easy 2 of 5" — the player's name for a phase. */
export function intervalPhaseName(phases: readonly TreadmillPhase[], idx: number): string {
  const p = phases[idx];
  if (!p || p.c === "rest") return "Warm-up";
  return `${p.c === "brisk" ? "Brisk" : "Easy"} ${intervalRound(idx)} of ${INTERVAL_ROUNDS}`;
}

/** Brisk rounds completed before phase `idx` — pass `phases.length` once every phase has run. A walk
 *  stopped during its third brisk stretch has done two. */
export function briskRoundsDone(phases: readonly TreadmillPhase[], idx: number): number {
  return phases.slice(0, Math.max(0, idx)).filter((p) => p.c === "brisk").length;
}

export interface IntervalPosition {
  idx: number;
  /** seconds left in that phase */
  leftSec: number;
  /** every round has run: the walk goes on, the rounds do not */
  over: boolean;
}

/**
 * Where an interval walk is, from its active seconds alone. The rounds start with the walk and a
 * pause stops both clocks, so the phase is a function of the walked time — the same arithmetic as
 * the wrapper's cue schedule, with no deadline to drift.
 */
export function intervalPosition(phases: readonly TreadmillPhase[], activeSec: number): IntervalPosition {
  let at = 0;
  for (let i = 0; i < phases.length; i++) {
    const end = at + phases[i].secs;
    if (activeSec < end) return { idx: i, leftSec: end - activeSec, over: false };
    at = end;
  }
  return { idx: Math.max(0, phases.length - 1), leftSec: 0, over: true };
}

/** Seconds of rounds still to come from `activeSec`, for "Round 2 of 5 · 18:00 to go". */
export function intervalSecsLeft(phases: readonly TreadmillPhase[], activeSec: number): number {
  const total = phases.reduce((a, p) => a + p.secs, 0);
  return Math.max(0, total - activeSec);
}

/* ---------- the cues ---------- */

export type CueId = "minutes" | "turn-back" | "planned-done" | "brisk" | "easy" | "rounds-done";

export interface Cue {
  /** active seconds from the start: paused time is left out */
  atSec: number;
  id: CueId;
}

export const CUE_EVERY_SECS = 300;
/** The schedule ends where the session's own life does: three hours (ADR-027). */
const CUE_HORIZON_SECS = SESSION_MAX_AGE_MS / 1000;

/**
 * Every cue a walk will speak, sorted by second.
 *
 * A free walk: "minutes" every five minutes up to and including three hours. With a planned
 * length, "turn-back" at half of it and "planned-done" at the end, each replacing a "minutes" cue
 * on the same second; the five-minute cues go on after the planned end, because the walk does.
 * An interval walk: "brisk" or "easy" at every phase boundary and "rounds-done" at the end of the
 * last round; no five-minute cues, which would talk over the rounds.
 */
export function cueSchedule(plannedMin: number | null, mode: "free" | "intervals"): Cue[] {
  if (mode === "intervals") {
    const out: Cue[] = [];
    const phases = intervalPhases();
    let at = 0;
    for (let i = 0; i < phases.length; i++) {
      if (i > 0) out.push({ atSec: at, id: phases[i].c === "brisk" ? "brisk" : "easy" });
      at += phases[i].secs;
    }
    out.push({ atSec: at, id: "rounds-done" });
    return out;
  }

  const by = new Map<number, CueId>();
  for (let at = CUE_EVERY_SECS; at <= CUE_HORIZON_SECS; at += CUE_EVERY_SECS) by.set(at, "minutes");
  if (typeof plannedMin === "number" && plannedMin > 0) {
    const end = Math.round(plannedMin * 60);
    const half = Math.round(end / 2);
    if (half <= CUE_HORIZON_SECS) by.set(half, "turn-back");
    if (end <= CUE_HORIZON_SECS) by.set(end, "planned-done");
  }
  return [...by.entries()].map(([atSec, id]) => ({ atSec, id })).sort((a, b) => a.atSec - b.atSec);
}

/**
 * The one cue to speak now: the latest whose second has passed since the last one spoken. A page
 * the phone throttled, or a wrapper that woke late, speaks the cue that is true now — never a
 * backlog of three — and a resume that passes `lastSec` never repeats one.
 */
export function dueCue(schedule: readonly Cue[], lastSec: number, nowSec: number): Cue | null {
  let found: Cue | null = null;
  for (const c of schedule) {
    if (c.atSec > nowSec) break;
    if (c.atSec > lastSec) found = c;
  }
  return found;
}

/** The next cue after `nowSec`, for the player's line ("Half-way at 15:00"); null when none. */
export function nextCue(schedule: readonly Cue[], nowSec: number): Cue | null {
  return schedule.find((c) => c.atSec > nowSec) ?? null;
}

/** 1600 → "1,600", the same on every phone and in the wrapper (no lakh grouping, no locale). */
function grouped(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * What a cue says. "minutes" is composed — "15 minutes · 1,600 steps", the steps left out when
 * nothing was measured — and the rest are the fixed words in `VOICE_WORDS`, so they can be clips.
 */
export function cueText(cue: CueId, lang: VoiceLang, elapsedMin: number, steps: number | null): string {
  if (cue === "minutes") {
    const mins = Math.max(0, Math.round(elapsedMin));
    const head = lang === "hi" ? `${mins} मिनट` : `${mins} minute${mins === 1 ? "" : "s"}`;
    if (typeof steps !== "number" || steps <= 0) return head;
    return `${head} · ${grouped(steps)} ${lang === "hi" ? "क़दम" : "steps"}`;
  }
  return voiceText(lang, cue) ?? "";
}
